import { parse } from 'cookie';
import { getSelectorsByUserAgent } from 'react-device-detect';
import { v4 as uuidv4 } from 'uuid';

import {
	getClientInitializationValues,
	ClientOptions,
	Identifiers,
} from '@atlassian/feature-gate-node-client-bootstrapper';
import type { Request as TesseractRequest } from '@atlassian/tesseract-runtime-globals';

import { ATL_COHORT, AtlCohort, Cohort } from '../analytics/atl-analytics/cohort';
import { enforceSafeCloudId } from '../utils/enforce-safe-cloud-id';
import { polyfillGetRandomValuesIfNecessary } from '../utils/polyfills';
import { sanitizeQueryParams } from '../utils/sanitize-query-params';
import { parseRoute } from '../wac-routes';

import { TARGET_APP } from './initialization';
import { FeatureGatesBootstrapValues } from './types';

export const ANALYTICS_ANONYMOUS_ID_COOKIE_KEY = 'ajs_anonymous_id';
export const ATLASSIAN_ACCOUNT_ID_COOKIE_KEY = '__aid_user_id';
let ATL_COHORT_COOKIE_VALUE: AtlCohort | undefined = undefined;
let ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE: string | undefined = undefined;

const DEVICE_TYPE = 'deviceType';

const FETCH_TIMEOUT_MS = 5000;

// @ts-ignore
const clientOptions: ClientOptions = {
	apiKey: String(process.env['FEATURE_GATES_KEY']),
	products: [],
};

export const getFeatureGateValues = async (
	request: Request | TesseractRequest,
): Promise<FeatureGatesBootstrapValues> => {
	const requestHeaders = request.headers;
	const identifiers: Identifiers = getIdentifiers(requestHeaders);
	const customAttributes = getCustomAttributes(request, identifiers.analyticsAnonymousId);

	// The Tesseract/snapvm SSR runtime exposes AbortSignal.timeout but does not
	// provide the `scheduler` global it depends on internally. The upstream
	// feature-gate-node-client-bootstrapper library checks for the method's
	// existence but not whether it works, so calling it throws. Remove the
	// broken method to force the library's AbortController-based fallback.
	if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
		try {
			AbortSignal.timeout(1);
		} catch {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			delete (AbortSignal as any).timeout;
		}
	}

	let response;
	try {
		// The timeout inside getClientInitializationValues relies on the AbortSignal.timeout
		// and AbortController APIs, neither of which are currently supported during SSR.
		// This custom timeout can be replaced once it is supported.
		let timeoutId: NodeJS.Timeout | undefined;
		const timeoutPromise = new Promise<never>((_resolve, reject) => {
			timeoutId = setTimeout(
				() =>
					reject(
						new Error(
							`getClientInitializationValues failed to complete within the ${FETCH_TIMEOUT_MS}ms timeout`,
						),
					),
				FETCH_TIMEOUT_MS,
			);
		});

		const fetchPromise: Promise<FeatureGatesBootstrapValues> = getClientInitializationValues(
			clientOptions,
			TARGET_APP,
			identifiers,
			customAttributes,
		)
			.then((featureGateValues) => {
				// Clean up server-only infrastructure attributes that client can't verify
				// These cause bootstrap mismatch warnings in Statsig because the client
				// has no way to compute/verify them
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const expValues = featureGateValues.experimentValues as any;
				if (expValues?.['user']?.['custom']) {
					const cleanedCustom = {
						...expValues['user']['custom'],
					};

					// Remove infrastructure-specific attributes
					delete cleanedCustom.microsEnv;
					delete cleanedCustom.microsAwsAvailabilityZone;
					delete cleanedCustom.userAgent;
					delete cleanedCustom.appVersion;

					expValues['user']['custom'] = cleanedCustom;
				}

				return {
					...featureGateValues,
					identifiers,
				};
			})
			.finally(() => {
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
			});

		response = await Promise.race([fetchPromise, timeoutPromise]);
	} catch (e) {
		const reason = e instanceof Error ? e.message : JSON.stringify(e);
		console.warn(
			`Could not fetch feature gate values, falling back to defaults. Reason: ${reason}`,
		);
		response = {
			identifiers,
		};
	}

	return response;
};

export const getIdentifiers = (headers: Headers | TesseractRequest['headers']): Identifiers => {
	// @ts-ignore
	const cookieHeader = headers['cookie'];
	const parsedCookies = cookieHeader ? parse(cookieHeader) : {};
	const rawCloudId = new URLSearchParams(window?.location?.search).get('cloudId');
	const tenantId = enforceSafeCloudId(rawCloudId) || undefined;

	return {
		// @ts-ignore
		analyticsAnonymousId: getOrGenerateAnalyticsAnonymousId(parsedCookies),
		// @ts-ignore
		atlassianAccountId: getAtlassianAccountId(parsedCookies),
		tenantId,
	};
};

export const getAtlCohort = () => ATL_COHORT_COOKIE_VALUE;

export const getAnalyticsAnonymousId = () => ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE;

/** Matches cookie.parse() return type (values can be undefined for missing keys) */
type ParsedCookies = Record<string, string | undefined>;

const getOrGenerateAnalyticsAnonymousId = (parsedCookies: ParsedCookies): string => {
	const anonymousIdCookieValue = parsedCookies[ANALYTICS_ANONYMOUS_ID_COOKIE_KEY];

	if (anonymousIdCookieValue) {
		// The cookie value is wrapped in quotes, so we need to remove those.
		ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE = decodeURIComponent(anonymousIdCookieValue).replace(
			/['"]+/g,
			'',
		);
		return ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE;
	}

	polyfillGetRandomValuesIfNecessary();
	ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE = uuidv4();
	// Check for presence of lambda edge - check key
	if (parsedCookies['X-Experiments-Key']) {
		const experimentsKey = parsedCookies['X-Experiments-Key'];
		if (
			window.location.pathname ===
			// @ts-ignore
			(experimentsKey.split('~')[0].split('=')[1] as string)
		) {
			// This console.log should never occur when request is through lambda edge.
			// Edge should be passing in the cookie
			console.log(
				'Generating new anonymous id cookie value ' + ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE,
			);
		}
	}
	return ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE;
};

/**
 * Get AtlCohort from cookie, if it doesn't exist, generate a new one
 * @param parsedCookies
 * @returns {"bucketAll":{"bucketedAtUTC":"2024-04-24T19:26:55.734Z","version":"2","index":74,"bucketId":0}}
 */
const getOrGenerateAtlCohort = (parsedCookies: ParsedCookies, anonymousId?: string): AtlCohort => {
	const atlCohortCookieValue = parsedCookies[ATL_COHORT];
	if (atlCohortCookieValue) {
		try {
			ATL_COHORT_COOKIE_VALUE = JSON.parse(decodeURIComponent(atlCohortCookieValue)) as AtlCohort;
			return ATL_COHORT_COOKIE_VALUE;
		} catch (e) {
			console.warn(
				`Could not parse ATL_COHORT cookie value: ${atlCohortCookieValue}, creating a new one.`,
				e,
			);
		}
	}
	const bucketAttribute = Cohort.createBucket('bucketAll', anonymousId);
	ATL_COHORT_COOKIE_VALUE = {
		bucketAll: bucketAttribute,
	};
	return ATL_COHORT_COOKIE_VALUE;
};

const getAtlassianAccountId = (parsedCookies: ParsedCookies): string | undefined => {
	const atlassianAccountIdCookieValue = parsedCookies[ATLASSIAN_ACCOUNT_ID_COOKIE_KEY];
	if (atlassianAccountIdCookieValue) {
		return decodeURIComponent(atlassianAccountIdCookieValue);
	}
	return undefined;
};

export type UserAttributes = {
	[ATL_COHORT]: number;
	[DEVICE_TYPE]: 'mobile' | 'tablet' | 'desktop' | 'other';
	platform: 'uip';
	country: string;
	region: string;
	loginUserDetected: boolean;
	queryParams: string;
	clusterTag: string;
	requestPath: string;
	browserName: string;
	browserVersion: string;
	browserOS: string;
	referer: string;
	locale: string;
	anchor: string;
	subAnchor: string;
};

export type RequestOverride = {
	headers: Record<'cookieOverride' | 'user-agent', string>;
	referrer?: string;
};

export const getCustomAttributes = (
	request: Request | TesseractRequest | RequestOverride,
	anonymousId?: string,
): UserAttributes => {
	const data: Partial<UserAttributes> = {};
	const headers = request.headers;
	// @ts-ignore
	const cookieHeader = headers['cookie'];
	// @ts-ignore
	const cookieOverrideHeader = headers['cookieOverride'];
	// @ts-ignore
	const userAgent = headers['user-agent'] || '';
	const parsedCookies = cookieHeader
		? parse(cookieHeader)
		: cookieOverrideHeader
			? JSON.parse(cookieOverrideHeader)
			: {};
	const atlCohort = getOrGenerateAtlCohort(parsedCookies, anonymousId);
	if (atlCohort) {
		data[ATL_COHORT] = atlCohort.bucketAll.index;
	}
	const { isTablet, isMobile, isDesktop, browserName, browserVersion, osName } =
		getSelectorsByUserAgent(userAgent) ?? {};
	if (isTablet) {
		data[DEVICE_TYPE] = 'tablet';
	} else if (isMobile) {
		data[DEVICE_TYPE] = 'mobile';
	} else if (isDesktop) {
		data[DEVICE_TYPE] = 'desktop';
	} else {
		data[DEVICE_TYPE] = 'other';
	}
	data.platform = 'uip';
	data.browserName = browserName;
	data.browserVersion = browserVersion;
	data.browserOS = osName;
	// Scrub Personal Data from the query string before exposing it to Statsig
	// as a user attribute. See utils/sanitize-query-params.ts and the PD
	// standard at https://hello.atlassian.net/wiki/spaces/PMP/pages/139161617.
	data.queryParams = sanitizeQueryParams(location.search);
	data.requestPath = location.pathname;
	// @ts-expect-error - Tesseract Request type does not have referrer
	data.referer = request.referrer;
	data.locale = parseRoute(globalThis.location).locale;
	data.anchor = parseRoute(globalThis.location).anchor;
	data.subAnchor = parseRoute(globalThis.location).subAnchor;

	const cloudSessionToken =
		parsedCookies['cloud.session.token'] || parsedCookies['cloud.session.token.stg'];
	data.loginUserDetected = cloudSessionToken !== undefined;
	// @ts-ignore
	data.edgeTraceId = parsedCookies['X-Experiments-Trace-Id'];
	return data as UserAttributes;
};
